import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import {HttpEvent, HttpResponse, HttpUploadProgressEvent} from '@angular/common/http';
import { Platform } from '@ionic/angular';
import {AppConfiguration, CurrentSettings, State, StateInterface} from '../../interfaces/biozoom-api/app-configuration';
import { ScannerStatus } from '../../interfaces/scanner-api/scanner-status';
import {catchError, delay, filter, first, repeat, switchMap, timeout} from 'rxjs/operators';
import { WebUsbApiService } from './web-usb-api.service';
import { PipelineStageConfig, StatusObject } from '../../interfaces/biozoom-api/pipeline';
import { UsbApiService2 } from './usb-api2.service';
import {ElectronUsbApiService} from './electron-usb-api.service';
import { UsbApiService } from './usb-api.service';
import {ScannerSignals} from '../../interfaces/scanner-api/scanner';
import {WifiApiService} from './wifi.service';
import {isVersionEqualOrNewer, updateInfNeeded} from '../../methods/helpers';
import {ApiProgressUtilsService} from "../api/api-progress-utils.service";

const expectedKeys = [
  'host',
  'product',
  'serial',
  'sensor',
  'sensor_firmware',
  'sensor_temp',
  'ip',
  'battery_voltage',
  'usb_voltage',
  'usb_charging',
  'memory_free_64k_blocks',
  'memory_healthy',
  'wifi_firmware',
  'wifi_name',
  'wifi_strength',
  'uptime',
  'svn',
  'timestamp',
  'production',
  'meas_count_wifi',
  'capabilities',
  'capabilities.SUPPORT_SPI_MEMORY',
  'capabilities.SUPPORT_CHARGE_CONTROL',
  'capabilities.SUPPORT_IDLE_TIMER',
  'capabilities.SUPPORT_MDNS',
  'capabilities.SUPPORT_USB_LEGACY_MODE',
  'capabilities.WITH_CHARGE_LED',
  'capabilities.AP_PASS',
  'capabilities.CORE_DEBUG_LEVEL'
];
const expectedKeysNew = [
  'nuvoton_firmware',
  'nuvoton_chip_id',
  'serial',
  'sensor',
  'battery_voltage',
  'usb_voltage',
  'usb_charging',
];

@Injectable({
  providedIn: 'root'
})
export class ScannerVersionApiService {
  private oldSerialRegEx = '[45]W[0-9A-F]{18}';
  private isUSB = false;
  private firstConnection = false;
  private isWeb = this.platform.is('desktop') && !this.platform.is('cordova');
  private isElectron: boolean;

  constructor(
    private platform: Platform,
    private electronUSB: ElectronUsbApiService,
    private webUSB: WebUsbApiService,
    private wifi: WifiApiService,
    private usb: UsbApiService,
    private usb2: UsbApiService2,
    private apiUtilsService: ApiProgressUtilsService,

  ) {
    this.isElectron = window && !!window.electronAPI;
  }

  async enableAP(state: StateInterface) {
    if (state.SCANNER_VERSION === 2) {
      return this.usb2.enableAP(state);
    }
    return this.usb.enableAP(state);
  }

  async disableAP(state: StateInterface) {
    if (state.SCANNER_VERSION === 2) {
      return this.usb2.disableAP(state);
    }
    return this.usb.disableAP(state);
  }

  async disableUSB(state: StateInterface) {
    return this.usb.disableUSB(state);
  }

  async enabledUSB(state: StateInterface, firstConnection = false, force = false) {
    return this.usb.enabledUSB(state, firstConnection, force);
  }

  async doHandrecognitionObservable(
    state: StateInterface, settings: CurrentSettings, handOff = false, wifiFirmware?: string
  ): Promise<boolean> {
    if (this.isElectron) {
      return this.electronUSB.doHandrecognitionObservable(state, settings, handOff);
    } else if (this.isWeb) {
      return this.webUSB.doHandrecognitionObservable(state, settings, handOff);
    } else if (state.CONNECTION_TYPE === 'WIFI') {
      return this.wifi.doHandRecognitionObservable(state, handOff, wifiFirmware);
    } else if (state.SCANNER_VERSION === 2) {
      const infFirmware = state.SCANNER_STATUS?.sensor_firmware ?? 1;
      if (infFirmware >= 604) {
        return this.usb2.doHandrecognitionObservable(state, settings, handOff, true);
      }
      return this.usb2.doHandrecognitionObservable(state, settings, handOff, false);
    }
    return this.usb.doHandrecognitionObservable(state, settings, handOff);
  }

  /**
   * Performs an AOX measurement.
   *
   * @returns An observable that emits HttpEvents during the measurement process.
   *                                      - The observable completes when the measurement is finished.
   * @param state state object
   * @param measurementType type of the usb measurement
   */
  measure(state: StateInterface, measurementType: string, oldApi = false): Observable<HttpEvent<any>> {
    if (state.DEMO_MODE) {
      let demoDelay = 1000;
      switch (measurementType) {
        case 'aox': demoDelay = 10000; break;
        case 'full': demoDelay = 10000; break;
        case 'front_half': demoDelay = 10000; break;
        case 'split': demoDelay = 10000; break;
        case 'hrv-test': demoDelay = 2000; break;
        case 'hrv': demoDelay = 10000; break;
        case 'hrvOfflineGet': demoDelay = 5000; break;
        case 'hrvOfflineDo': demoDelay = 5000; break;
      }
      return this.apiUtilsService.generateDownloadProgress(demoDelay, new HttpResponse<any>({ body: '' }));
    }
    if (measurementType.startsWith('hrv') &&
      ((this.isWeb && !this.isElectron) || (state.USB_MODE && state.SCANNER_VERSION !== 2))
    ) {
      throw new Error('Not supported');
    }
    if (this.isElectron) {
      return this.electronUSB.doAOXMeasurementObservable(measurementType);
    } else if (this.isWeb) {
      return this.webUSB.doAOXMeasurementObservable(measurementType);
    } else if (state.CONNECTION_TYPE === 'WIFI') {
      let gen1Type = measurementType;
      if (!(gen1Type.startsWith('hrv'))){
        gen1Type = 'aox';
      }
      return this.wifi.doAOXMeasurementObservable(measurementType, state, gen1Type, state?.SCANNER_VERSION === 2, oldApi);
    } else if (state.SCANNER_VERSION === 2) {
      return this.usb2.doAOXMeasurementObservable(state, measurementType);
    }
    return this.usb.doAOXMeasurementObservable(state, measurementType);
  }

  async getStatus(state: StateInterface): Promise<ScannerStatus> {
    if (state.DEMO_MODE) {
      return {
        battery_voltage: 4,
        host: 'biozoom_dummy',
        product: 'VitalityCheck',
        ip: '192.168.4.1',
        sensor: '',
        sensor_firmware: '',
        serial: '',
        svn: '',
        timestamp: '',
        uptime: 100,
        wifi_firmware: '',
        wifi_name: '',
        wifi_strength: 80
      };
    } else if (this.isElectron) {
      return this.electronUSB.getStatus(state);
    } else if (this.isWeb) {
      return this.webUSB.getStatus(state);
    } else if (state.CONNECTION_TYPE === 'WIFI') {
      return this.wifi.getStatus();
    } else if (state.SCANNER_VERSION === 2) {
      return this.usb2.getStatus(state);
    }
    return this.usb.getStatus(state);
  }

  async disconnectScanner(state: StateInterface) {
    console.log('Disconnecting scanner');
    if (this.isWeb) {
      return;
    } else if (state.CONNECTION_TYPE === 'WIFI') {
      return this.wifi.disconnectScanner(state);
    } else if (state.SCANNER_VERSION === 2) {
      return this.usb2.disconnectScanner(state);
    }
    return this.usb.disconnectScanner(state);
  }

  async deleteFlash(pipelineConfig: PipelineStageConfig) {
    if (this.isWeb) {
      // TODO
      return true;
    } else if (pipelineConfig?.state?.persistentValues?.CONNECTION_TYPE === 'WIFI') {
      return this.wifi.deleteFlash(pipelineConfig);
    } else if (pipelineConfig?.state?.persistentValues?.SCANNER_VERSION === 2) {
      return this.usb2.deleteFlash(pipelineConfig.state.persistentValues);
    }
    return this.usb.deleteFlash(pipelineConfig.state.persistentValues);
  }

  async connectToScanner(
    state: StateInterface,
    setState: ((key: State, val: any) => void),
    setBlockScreen: ((block: boolean) => void) = (() => { }),
    usbOnly: boolean = false,
  ): Promise<[boolean, string, number]> {
    this.isElectron = window && !!window.electronAPI;
    let res: [string, number];
    if (state.DEMO_MODE) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      return [true, '', 0];
    } else if (this.isElectron) {
      res = await this.electronUSB.requestPermission(state, setState, setBlockScreen);
    } else if (this.isWeb) {
      res = await this.webUSB.requestPermission(state, setState, setBlockScreen);
    } else if (state.CONNECTION_TYPE === 'WIFI') {
      if (usbOnly) {
        return [false, '', 0];
      }
      return this.wifi.connectScanner(state);
    } else if (!this.platform.is('cordova') || !this.platform.is('android')) {
      res = ['', 0];
    } else {
      res = await this.usb.checkPermissionAndOpenSerialConnection(state, setState, setBlockScreen);
    }
    return [res[0] !== '', res[0], res[1]];
  }

  /**
   * Sends a generic command with a payload, ensuring the connection is prepared first.
   * This is currently only implemented for Electron.
   * @param state - The current state object.
   * @param command - The command string to send.
   * @param receivedData - The additional data (object, string, or Buffer).
   * @returns A promise that resolves with the serial write result.
   */
  async executeGenericCommand(state: StateInterface, command: string, receivedData: any) {
    if (this.isElectron) {
      return this.electronUSB.executeGenericCommand(command, receivedData);
    } else if (this.isWeb) {
      throw new Error('executeGenericCommand not implemented for WebUSB');
    } else if (state.CONNECTION_TYPE === 'WIFI') {
      throw new Error('executeGenericCommand not implemented for WIFI');
    }
    // Fallback for USB/ Android
    throw new Error('executeGenericCommand not implemented for legacy USB');
  }

  updateESP(pipelineConfig: PipelineStageConfig, method: string, toggleUSB: boolean, downgrade: boolean) {
    if (method === 'usb2') {
      return this.usb2.updateESP(pipelineConfig);
    } else if (method === 'wifi') {
      return switchMap((_stati: StatusObject): Observable<{started: boolean, target: string}> => {
        return of({}).pipe(
          switchMap(async () => {
            let status = true;
            let wifi: any;
            if (toggleUSB) {
              const {status: s, wifi: w} = await this.enableAP(pipelineConfig.state.persistentValues);
              status = s;
              wifi = w;
            }
            if (!status) {
              return {
                started: false,
                target: 'onNoWifi',
              };
            }
            if (wifi) {
              pipelineConfig.setState(State.SCANNER_WIFI, wifi);
            }
            const isUpdated = await this.wifi.updateESP(pipelineConfig, downgrade);
            if (toggleUSB) {
              await this.disableAP(pipelineConfig.state.persistentValues);
            }
            return {
              started: isUpdated,
              target: isUpdated ? 'onSuccess' : 'onError',
            };
          })
        );
      });
    }
    return switchMap((_stati: StatusObject): Observable<{started: boolean, target: string}> => {
      return of({
        started: false,
        target: 'onError',
      });
    });
    // return switchMap((_stati: StatusObject): Observable<boolean> => {
    //   return of({}).pipe(
    //     switchMap(async () => {
    //       if (serial && serial !== '') {
    //         let isUpdated = true;
    //         if (Number(serial.slice(2, 4)) > 20) {
    //           const { status, wifi } = await this.enableAP(pipelineConfig.state.persistentValues);
    //           if (!status) {
    //             pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onNoWifi, 'onNoWifi', pipelineConfig.stage);
    //             return false;
    //           }
    //           pipelineConfig.setState(State.SCANNER_WIFI, wifi);
    //           isUpdated = await this.wifi.updateESP(pipelineConfig);
    //           await this.disableAP(pipelineConfig.state.persistentValues);
    //         } else {
    //           pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onNotNeeded, 'onNotNeeded', pipelineConfig.stage);
    //           return true;
    //         }
    //         if (isUpdated) {
    //           pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
    //           return true;
    //         }
    //       }
    //       pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onError, 'onError', pipelineConfig.stage);
    //       return true;
    //     })
    //   );
    // });
  }

  updateNuvoton(pipelineConfig: PipelineStageConfig) {
    return this.usb2.updateNuv(pipelineConfig);
  }

  updateInfineon(pipelineConfig: PipelineStageConfig, bootloader: boolean) {
    if (!pipelineConfig.state.persistentValues.DEMO_MODE && pipelineConfig.state.persistentValues.CONNECTION_TYPE === 'WIFI') {
      return switchMap((_stati: StatusObject): Observable<{started: boolean, target: string}> => {
        const x = pipelineConfig.state.persistentValues.SCANNER_STATUS;
        const major = Number(x.wifi_firmware.split('.')[0]);
        const minor = Number(x.wifi_firmware.split('.')[1]);
        if (minor < 4 && (major === 10 || major === 20)) {
          // pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onError, 'onError', pipelineConfig.stage);
          return of({started: false, target: 'onError'});
        } else if (!updateInfNeeded(
          pipelineConfig?.state?.persistentValues?.SCANNER_VERSION,
          pipelineConfig?.state?.persistentValues?.SCANNER_STATUS?.sensor_firmware,
          pipelineConfig?.state?.persistentValues?.SCANNER_STATUS?.wifi_firmware,
          pipelineConfig.config
        )) {
          return of({started: false, target: 'onNotNeeded'});
        }
        return this.wifi.updateInfineon(pipelineConfig, bootloader).pipe(
          timeout(60000), // Timeout after 45 seconds
          delay(2000),
          switchMap((status) => {
            return of({}).pipe(
              switchMap(async (s) => {
                if (bootloader) {
                  return true;
                }
                const scannerStatus = await this.wifi.getStatus();
                if (scannerStatus) {
                  pipelineConfig.setStep(
                    { progress: (scannerStatus?.progress ?? 0) / 100, idx: 0 }
                  );
                  if (scannerStatus?.progress === 100) {
                    return true;
                  }
                }

                return false;
              }),
              repeat(),
              filter((el) => !!el),
            );
          }),
          first(),
          switchMap(async (status) => {
            // pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
            return {started: true, target: 'onSuccess'};
          }),
          catchError(async (err) => {
            // pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onError, 'onError', pipelineConfig.stage);
            return {started: false, target: 'onError'};
          })
        );
      });
    }
    return this.usb2.updateInf(pipelineConfig, bootloader);
  }

  setSignalOnOff(on: ScannerSignals[], off: ScannerSignals[], state: StateInterface) {
    if (state.DEMO_MODE || state.USB_MODE) {
      return of('');
    }
    return this.wifi.setSignalOnOff(on, off, state);
  }

  // doHrvOfflineMeasurement(state: StateInterface, full: boolean, v2: boolean) {
  //   if (state.DEMO_MODE) {
  //     return this.apiUtilsService.generateDownloadProgress(5000, new HttpResponse<any>({ body: '' }));
  //   }
  //   if (state.CONNECTION_TYPE === 'WIFI') {
  //     return this.wifi.doHrvOfflineMeasurement(state, full, v2);
  //   }
  //   return of<HttpUploadProgressEvent>();
  // }
  //
  // getHrvOfflineMeasurement(command: string, state: StateInterface) {
  //   if (state.DEMO_MODE) {
  //     return this.apiUtilsService.generateDownloadProgress(5000, new HttpResponse<any>({ body: '' }));
  //   }
  //   return this.wifi.getHrvOfflineMeasurement(command, state);
  // }

  // updateScannerFirmwareOffline(filename, state: StateInterface) {
  //   if (state.DEMO_MODE || state.CONNECTION_TYPE !== 'WIFI') {
  //     return of<string>('');
  //   }
  //   return this.wifi.updateScannerFirmwareOffline(filename, state);
  // }

  getIdent(state: StateInterface) {
    if (state.DEMO_MODE) {
      return of<string>('');
    } else if (state.CONNECTION_TYPE === 'WIFI') {
      return this.wifi.getIdent();
    }
    return of(state.SCANNER_SERIAL);
  }

  async checkScannerFirmware(state: StateInterface, config: AppConfiguration) {
    if (state.DEMO_MODE) {
      return 'firmware_1_8_0';
    }
    const status = await this.getStatus(state);
    return isVersionEqualOrNewer(status, config);
  }

  finalizeScanner(pipelineConfig: PipelineStageConfig) {
    if (
      pipelineConfig.state.persistentValues.DEMO_MODE
    ) {
      return true;
    }
    if (this.isElectron) {
      return this.electronUSB.finalizeScanner(pipelineConfig);
    } else if (this.isWeb) {
      return this.webUSB.finalizeScanner(pipelineConfig);
    }
    return true;
  }
}
